import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const Header: React.FC = () => {
  const { isAuthenticated, logout, user } = useAuth();
  const isSeller = isAuthenticated && !!user?.is_seller;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadChats, setUnreadChats] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // عدّادات الإشعارات والمحادثات غير المقروءة (تتحدّث عند التنقّل وكل دقيقة)
  useEffect(() => {
    if (!isAuthenticated) { setUnreadCount(0); setUnreadChats(0); return; }
    let alive = true;
    const load = () => {
      api.get('/notifications/unread-count')
        .then(r => { if (alive) setUnreadCount(r.data?.data?.unread_count || 0); })
        .catch(() => {});
      api.get('/conversations/unread-count')
        .then(r => { if (alive) setUnreadChats(r.data?.data?.unread_count || 0); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [isAuthenticated, location.pathname]);

  const countBadge: React.CSSProperties = {
    position: 'absolute', top: 1, insetInlineEnd: 1, minWidth: 16, height: 16, padding: '0 4px',
    borderRadius: 9999, background: '#E53935', color: '#fff', fontSize: 10, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, boxShadow: '0 0 0 2px #fff',
  };

  const navLinks = [
    { to: '/', label: 'الرئيسية' },
    { to: '/ads', label: 'السوق' },
    { to: '/categories', label: 'التصنيفات' },
    { to: '/services', label: 'الخدمات' },
  ];

  const isActive = (path: string) => location.pathname === path;

  // إغلاق القوائم عند تغيير الصفحة
  useEffect(() => {
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const iconBtn: React.CSSProperties = {
    width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: 18, color: '#546E7A', textDecoration: 'none',
    background: '#F4F7F6', transition: 'all 0.15s', position: 'relative',
  };

  const menuItem: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', fontSize: 14,
    color: '#37474F', textDecoration: 'none', fontFamily: "'Cairo', sans-serif", fontWeight: 600,
    whiteSpace: 'nowrap', transition: 'background 0.15s',
  };

  return (
    <header style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', position: 'sticky', top: 0, zIndex: 2000, fontFamily: "'Tajawal', sans-serif", borderBottom: '1px solid #E6ECE9' }}>
      <div style={{ maxWidth: 1360, margin: '0 auto', padding: '0 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, minHeight: 72 }}>
          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', flexShrink: 0 }}>
            <img src="/aljalab-logo.png" alt="الجلب" style={{ height: 42, width: 'auto', display: 'block' }} />
            <span style={{ fontSize: 12, color: '#6E7C76', fontWeight: 500 }} className="brand-sub">سوق الحلال</span>
          </Link>

          {/* Desktop Nav */}
          <nav style={{ display: 'flex', alignItems: 'center', gap: 2 }} className="desktop-nav">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                style={{
                  padding: '0 14px', height: 40, display: 'flex', alignItems: 'center', borderRadius: 10, fontSize: 15, fontWeight: isActive(link.to) ? 700 : 500,
                  color: isActive(link.to) ? '#005343' : '#3E4C46',
                  background: isActive(link.to) ? '#E7F2EE' : 'transparent',
                  textDecoration: 'none', transition: 'all 0.15s', whiteSpace: 'nowrap',
                }}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Search */}
          <div style={{ flex: 1, maxWidth: 360, minWidth: 120 }} className="desktop-search">
            <input
              type="text"
              placeholder="ابحث عن مواشي، خدمات..."
              onFocus={() => navigate('/search')}
              style={{
                width: '100%', background: '#F4F7F6', border: '1.5px solid #E4EBE9',
                borderRadius: 10, padding: '9px 16px', fontSize: 13,
                fontFamily: "'Cairo', sans-serif", outline: 'none', color: '#37474F',
              }}
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }} className="desktop-actions">
            {isAuthenticated ? (
              <>
                <Link to="/my/favorites" style={iconBtn} title="المفضلة">♡</Link>
                <Link to="/my/conversations" style={iconBtn} title="المحادثات">
                  💬
                  {unreadChats > 0 && (
                    <span style={countBadge}>{unreadChats > 9 ? '9+' : unreadChats}</span>
                  )}
                </Link>
                <Link to="/my/notifications" style={iconBtn} title="الإشعارات">
                  🔔
                  {unreadCount > 0 && (
                    <span style={countBadge}>{unreadCount > 9 ? '9+' : unreadCount}</span>
                  )}
                </Link>

                <Link
                  to="/ads/create"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, background: '#005343',
                    color: '#fff', padding: '9px 18px', borderRadius: 10, fontSize: 13,
                    fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap',
                    boxShadow: '0 2px 6px rgba(17,94,84,0.25)',
                  }}
                >
                  + أضف إعلان
                </Link>

                {/* User menu */}
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setUserMenuOpen(o => !o)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, background: userMenuOpen ? '#E6F2F0' : '#F4F7F6',
                      border: 'none', borderRadius: 30, padding: '4px 10px 4px 4px', cursor: 'pointer',
                      fontFamily: "'Cairo', sans-serif",
                    }}
                    title={user?.name}
                  >
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#005343', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, overflow: 'hidden', flexShrink: 0 }}>
                      {user?.avatar ? <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (user?.name?.charAt(0) || '؟')}
                    </div>
                    <span style={{ fontSize: 11, color: '#78909C' }}>▾</span>
                  </button>

                  {userMenuOpen && (
                    <>
                      <div onClick={() => setUserMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 60 }} />
                      <div style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, minWidth: 210, background: '#fff', borderRadius: 12, boxShadow: '0 8px 28px rgba(0,0,0,0.14)', border: '1px solid #ECEFF1', overflow: 'hidden', zIndex: 61, padding: '6px 0' }}>
                        <div style={{ padding: '8px 16px 10px', borderBottom: '1px solid #F0F3F2', marginBottom: 4 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#263238' }}>{user?.name}</div>
                          <div style={{ fontSize: 11, color: '#90A4AE' }}>{isSeller ? (user?.account_type === 'company' ? 'حساب شركة' : 'بائع فرد') : 'عضو'}</div>
                        </div>
                        <Link to="/my/profile" style={menuItem}>⚙️ إعدادات الحساب</Link>
                        <Link to="/my/ads" style={menuItem}>📋 إعلاناتي</Link>
                        {isSeller && <Link to="/my/featured" style={{ ...menuItem, color: '#B45309' }}>✨ فئة التميّز</Link>}
                        {isSeller && <Link to="/commission" style={menuItem}>🧮 حاسبة العمولات</Link>}
                        {isSeller && <Link to="/my/support" style={menuItem}>🎧 الدعم الفني</Link>}
                        <div style={{ borderTop: '1px solid #F0F3F2', margin: '4px 0' }} />
                        <button onClick={() => { logout(); setUserMenuOpen(false); navigate('/'); }} style={{ ...menuItem, width: '100%', border: 'none', background: 'none', cursor: 'pointer', color: '#E53935', textAlign: 'right' }}>↩ تسجيل الخروج</button>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/login" style={{ padding: '8px 16px', fontSize: 14, color: '#005343', textDecoration: 'none', fontWeight: 700 }}>دخول</Link>
                <Link
                  to="/ads/create"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#005343', color: '#fff', padding: '9px 18px', borderRadius: 10, fontSize: 13, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap', boxShadow: '0 2px 6px rgba(17,94,84,0.25)' }}
                >
                  + أضف إعلان
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="mobile-menu-btn"
            style={{ display: 'none', background: 'none', border: 'none', fontSize: 22, color: '#546E7A', cursor: 'pointer', padding: 8, marginRight: 'auto' }}
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>

        {/* Mobile Search */}
        <div className="mobile-search" style={{ display: 'none', paddingBottom: 12 }}>
          <input
            type="text"
            placeholder="ابحث عن مواشي، خدمات..."
            onFocus={() => navigate('/search')}
            style={{ width: '100%', background: '#F4F7F6', border: '1.5px solid #E4EBE9', borderRadius: 10, padding: '9px 16px', fontSize: 13, fontFamily: "'Cairo', sans-serif", outline: 'none' }}
          />
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div style={{ background: '#fff', borderTop: '1px solid #ECEFF1', padding: '10px 20px' }} className="mobile-menu">
          {navLinks.map(link => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMobileMenuOpen(false)}
              style={{ display: 'block', padding: '11px 0', fontSize: 15, fontWeight: isActive(link.to) ? 700 : 600, color: isActive(link.to) ? '#005343' : '#37474F', textDecoration: 'none', borderBottom: '1px solid #F5F5F5' }}
            >
              {link.label}
            </Link>
          ))}
          {isAuthenticated ? (
            <>
              <Link to="/my/profile" onClick={() => setMobileMenuOpen(false)} style={{ display: 'block', padding: '11px 0', fontSize: 15, fontWeight: 600, color: '#37474F', textDecoration: 'none', borderBottom: '1px solid #F5F5F5' }}>⚙️ إعدادات الحساب</Link>
              <Link to="/my/ads" onClick={() => setMobileMenuOpen(false)} style={{ display: 'block', padding: '11px 0', fontSize: 15, color: '#37474F', textDecoration: 'none', borderBottom: '1px solid #F5F5F5' }}>📋 إعلاناتي</Link>
              <Link to="/my/favorites" onClick={() => setMobileMenuOpen(false)} style={{ display: 'block', padding: '11px 0', fontSize: 15, color: '#37474F', textDecoration: 'none', borderBottom: '1px solid #F5F5F5' }}>♡ المفضلة</Link>
              <Link to="/my/conversations" onClick={() => setMobileMenuOpen(false)} style={{ display: 'block', padding: '11px 0', fontSize: 15, color: '#37474F', textDecoration: 'none', borderBottom: '1px solid #F5F5F5' }}>💬 المحادثات {unreadChats > 0 && <span style={{ background: '#E53935', color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 9999, padding: '1px 7px', marginInlineStart: 6 }}>{unreadChats > 9 ? '9+' : unreadChats}</span>}</Link>
              <Link to="/my/notifications" onClick={() => setMobileMenuOpen(false)} style={{ display: 'block', padding: '11px 0', fontSize: 15, color: '#37474F', textDecoration: 'none', borderBottom: '1px solid #F5F5F5' }}>🔔 الإشعارات {unreadCount > 0 && <span style={{ background: '#E53935', color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 9999, padding: '1px 7px', marginInlineStart: 6 }}>{unreadCount > 9 ? '9+' : unreadCount}</span>}</Link>
              {isSeller && (
                <>
                  <Link to="/my/featured" onClick={() => setMobileMenuOpen(false)} style={{ display: 'block', padding: '11px 0', fontSize: 15, fontWeight: 600, color: '#B45309', textDecoration: 'none', borderBottom: '1px solid #F5F5F5' }}>✨ فئة التميّز</Link>
                  <Link to="/commission" onClick={() => setMobileMenuOpen(false)} style={{ display: 'block', padding: '11px 0', fontSize: 15, color: '#37474F', textDecoration: 'none', borderBottom: '1px solid #F5F5F5' }}>🧮 حاسبة العمولات</Link>
                  <Link to="/my/support" onClick={() => setMobileMenuOpen(false)} style={{ display: 'block', padding: '11px 0', fontSize: 15, color: '#37474F', textDecoration: 'none', borderBottom: '1px solid #F5F5F5' }}>🎧 الدعم الفني</Link>
                </>
              )}
              <button onClick={() => { logout(); setMobileMenuOpen(false); navigate('/'); }} style={{ display: 'block', width: '100%', textAlign: 'right', padding: '11px 0', fontSize: 15, color: '#E53935', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Cairo', sans-serif" }}>↩ تسجيل الخروج</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setMobileMenuOpen(false)} style={{ display: 'block', padding: '11px 0', fontSize: 15, fontWeight: 700, color: '#005343', textDecoration: 'none' }}>تسجيل الدخول</Link>
          )}
        </div>
      )}

      <style>{`
        .desktop-nav a:hover{background:#F4F7F6}
        .desktop-actions a[title]:hover{background:#E6F2F0;color:#005343}
        @media(max-width:900px){
          .desktop-nav{display:none!important}
        }
        @media(max-width:600px){ .brand-sub{display:none!important} }
        @media(max-width:768px){
          .desktop-nav,.desktop-search,.desktop-actions{display:none!important}
          .mobile-menu-btn,.mobile-search{display:block!important}
        }
      `}</style>
    </header>
  );
};

export default Header;
